import {
  HOMEPAGE_TRANSITION_NAME,
  SWEEP_DURATION_MS,
  SWEEP_EASING,
  SWEEP_LINE_FADE_MS,
  SWEEP_LINE_TRANSITION_NAME,
  SWITCHING_ATTRIBUTE,
} from "./constants";
import { lockScroll } from "./scrollLock";

/**
 * Whether this browser and this student get the animated switch. Everyone else
 * gets the instant swap: browsers without the View Transitions API, students who
 * ask for reduced motion, and background tabs (which can't run a transition).
 */
export const canRunBannerSweep = () =>
  typeof document.startViewTransition === "function" &&
  document.visibilityState === "visible" &&
  !window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

// Starting a sweep skips the one already running; only the latest may clean up.
let latestSweepId = 0;

const insetFromTop = (px) => `inset(${px}px 0px 0px 0px)`;
const insetFromBottom = (px) => `inset(0px 0px calc(100% - ${px}px) 0px)`;

/**
 * The "banner sweep": a blue line runs from the top of the homepage area (just
 * under the banner) to the bottom of the screen, revealing the other homepage
 * above it and keeping the old one below it.
 *
 * Built on the View Transitions API, so both homepages are flat snapshots while
 * it runs and neither re-renders per frame. The line is a real element present
 * only in the "old" snapshot: `update` removes it, so it can never linger in the
 * live page after the animation ends.
 *
 * `update` also scrolls to the top, so the other homepage always starts there.
 * The old snapshot is held where the student left it (the browser would
 * otherwise draw it at the new position), so nothing jumps when they had
 * scrolled down.
 *
 * The page can't be scrolled while this runs: the "new" snapshot is live, so
 * scrolling would move the other homepage out from under the line.
 *
 * The homepage area only gets its view-transition-name while this runs (see
 * SWITCHING_ATTRIBUTE in style.module.css), so nothing else on the page, and no
 * other page, is affected.
 *
 * @param {Object} params
 * @param {HTMLElement} params.area - the element holding both homepages
 * @param {string} params.lineClassName - styles the line and names it for the transition
 * @param {() => (void|Promise<void>)} params.update - shows the other homepage and
 *   scrolls to the top; must change the DOM synchronously (flushSync), may then wait
 * @param {() => void} [params.onFinished] - the sweep is over (or was skipped);
 *   not called for a sweep that a newer one replaced
 */
export const runBannerSweep = ({ area, lineClassName, update, onFinished }) => {
  latestSweepId += 1;
  const sweepId = latestSweepId;
  const root = document.documentElement;
  const before = area.getBoundingClientRect();
  const oldTop = before.top;
  // Where the area sits once scrolled to the top: that is where the line starts.
  const lineTop = Math.max(oldTop + window.scrollY, 0);
  const travel = Math.max(window.innerHeight - lineTop, 0);
  let newTop = lineTop;

  const line = document.createElement("div");
  line.className = lineClassName;
  line.setAttribute("aria-hidden", "true");
  line.style.top = `${lineTop}px`;
  line.style.left = `${before.left}px`;
  line.style.width = `${before.width}px`;
  document.body.appendChild(line);
  root.setAttribute(SWITCHING_ATTRIBUTE, "");
  const scrollLock = lockScroll();

  const transition = document.startViewTransition(async () => {
    line.remove();
    const updated = update();
    // update scrolled to the top (synchronously): hold the page there.
    scrollLock.holdHere();
    await updated;
    newTop = area.getBoundingClientRect().top;
  });

  transition.ready
    .then(() => {
      const timing = {
        duration: SWEEP_DURATION_MS,
        easing: SWEEP_EASING,
        fill: "both",
      };
      const lineEnd = lineTop + travel;
      // Each snapshot is clipped in its own coordinates, at the line.
      root.animate(
        {
          clipPath: [
            insetFromBottom(lineTop - newTop),
            insetFromBottom(lineEnd - newTop),
          ],
        },
        {
          ...timing,
          pseudoElement: `::view-transition-new(${HOMEPAGE_TRANSITION_NAME})`,
        },
      );
      const holdOldInPlace = `translateY(${oldTop - newTop}px)`;
      root.animate(
        {
          clipPath: [insetFromTop(lineTop - oldTop), insetFromTop(lineEnd - oldTop)],
          transform: [holdOldInPlace, holdOldInPlace],
        },
        {
          ...timing,
          pseudoElement: `::view-transition-old(${HOMEPAGE_TRANSITION_NAME})`,
        },
      );
      const linePseudo = `::view-transition-old(${SWEEP_LINE_TRANSITION_NAME})`;
      root.animate(
        { transform: ["translateY(0px)", `translateY(${travel}px)`] },
        { ...timing, pseudoElement: linePseudo },
      );
      root.animate(
        { opacity: [1, 0] },
        {
          duration: SWEEP_LINE_FADE_MS,
          delay: SWEEP_DURATION_MS,
          easing: "linear",
          fill: "both",
          pseudoElement: linePseudo,
        },
      );
    })
    // Skipped (another transition started, or the tab was hidden): the update still ran.
    .catch(() => {});

  transition.finished
    .catch(() => {})
    .then(() => {
      line.remove();
      scrollLock.release();
      // A newer sweep took over: leave the attribute and the "finished" to it.
      if (sweepId !== latestSweepId) return;
      root.removeAttribute(SWITCHING_ATTRIBUTE);
      onFinished?.();
    });
};
